import * as React from "react";
import {useEffect, useState} from "react";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import { Divider, Menu, MenuItem, Box, Typography } from "@mui/material";
import { ManageAccountsOutlined, LogoutOutlined, PersonOutlined } from "@mui/icons-material";
import styles from "./fileTree.module.css";
import { useTranslation } from "react-i18next";
import DriveService from "../../services/DriveService";
import AuthService from "../../services/AuthService";
import useDrive from "../../hooks/useDrive";
import {DRIVE_TYPES} from "../../config/appConst";
import {APP_DATA} from "../../config/settings";
import StorageStatus from "../StorageStatus";
import {
  HomeOutlined,
  FolderSharedOutlined,
  PeopleAltOutlined,
  FolderOutlined,
  ChevronRightOutlined,
  MedicalServicesOutlined,
  InsertDriveFileOutlined,
  ViewInArOutlined,
  PersonAddOutlined,
  GroupOutlined,
  CloudDoneOutlined,
  DesktopWindowsOutlined,
  FileDownloadOutlined,
} from "@mui/icons-material";


/* ── Icon map: id → MUI icon component ── */
const ICONS = {
  home:       HomeOutlined,
  patients:   PeopleAltOutlined,
  xrays:      MedicalServicesOutlined,
  models3d:   ViewInArOutlined,
  reports:    InsertDriveFileOutlined,
  shared:     FolderSharedOutlined,
  folder:     FolderOutlined,
  newPatient: PersonAddOutlined,
  myPatients: GroupOutlined,
};

/* ── Category colour tokens ── */
const CAT_COLORS = {
  home:       { bg: "rgba(232,88,120,0.15)",  icon: "#e85878" },
  patients:   { bg: "rgba(251,191,36,0.12)",  icon: "#fbbf24" },
  xrays:      { bg: "rgba(34,211,238,0.12)",  icon: "#22d3ee" },
  reports:    { bg: "rgba(52,211,153,0.12)",  icon: "#34d399" },
  models3d:   { bg: "rgba(124,106,247,0.15)", icon: "#7c6af7" },
  shared:     { bg: "rgba(52,211,153,0.10)",  icon: "#34d399" },
  folder:     { bg: "rgba(255,255,255,0.07)", icon: "#8892b0" },
  newPatient: { bg: "rgba(232,88,120,0.15)",  icon: "#e85878" },
  myPatients: { bg: "rgba(251,191,36,0.12)",  icon: "#fbbf24" },
};

/* ── NavItem ── */
function NavItem({ iconKey, label, active, onClick, expanded, children, depth = 0 }) {
  const [open, setOpen] = useState(false);
  const hasChildren     = React.Children.count(children) > 0;
  const cat             = CAT_COLORS[iconKey] || CAT_COLORS.folder;
  const IconComp        = ICONS[iconKey] || FolderOutlined;

  const handleClick = () => {
    if (hasChildren) setOpen(v => !v);
    onClick?.();
  };

  return (
    <>
      <Tooltip title={!expanded ? label : ""} placement="right" arrow>
        <div
          className={`${styles.navItem} ${active ? styles.navItemActive : ""}`}
          style={{ paddingLeft: expanded ? `${18 + depth * 14}px` : undefined }}
          onClick={handleClick}
        >
          <span className={styles.navDot} />
          <span
            className={styles.navIcon}
            style={{ background: cat.bg }}
          >
            <IconComp sx={{ fontSize: 26, color: cat.icon }} />
          </span>
          {expanded && <span className={styles.navLabel}>{label}</span>}
          {expanded && hasChildren && (
            <span className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}>
              <ChevronRightOutlined sx={{ fontSize: 14 }} />
            </span>
          )}
        </div>
      </Tooltip>

      {hasChildren && expanded && (
        <Collapse in={open}>
          <div className={styles.subNav}>{children}</div>
        </Collapse>
      )}
    </>
  );
}

/* ── Sub-folder item ── */
function SubItem({ label, active, onClick, expanded }) {
  return (
    <div
      className={`${styles.subNavItem} ${active ? styles.subNavItemActive : ""}`}
      onClick={onClick}
    >
      <FolderOutlined sx={{ fontSize: 14, color: active ? "#e85878" : "var(--text-3)", flexShrink: 0 }} />
      {expanded && <span className={styles.subNavItemLabel}>{label}</span>}
    </div>
  );
}

export default function DriveSidebar({ expanded, onToggle, onNavigate }) {
  const { t } = useTranslation();
  const { CurrentDrive, changeCurrentDrive, deleteKey } = useDrive();
  const [tree, setTree]             = useState([]);
  const [sharedTree, setSharedTree] = useState([]);
  const [active, setActive]         = useState("home");
  const [user, setUser]             = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);

  useEffect(() => { AuthService.user().then(setUser); }, []);

  const logOut = () => {
    const url = window.location.origin + "/#" + APP_DATA.LOGIN_ROUTE;
    window.location.href = url;
    localStorage.clear();
  };

  const initials = user
    ? `${user.Name?.[0] || ""}${user.FamilyName?.[0] || ""}`.toUpperCase()
    : "?";

  useEffect(() => { loadFolders("", ""); }, []);

  useEffect(() => {
    if (CurrentDrive) loadSelected(CurrentDrive);
  }, [CurrentDrive]);

  useEffect(() => {
    if (deleteKey) setTree(t => t.filter(n => n.meAsParent !== deleteKey));
  }, [deleteKey]);

  const loadFolders = async (parent = "", driveType = "") => {
    const res = await DriveService.getFileTree(parent, driveType);
    changeCurrentDrive({ parent, drive: res, driveType });
  };

  const loadShared = async () => {
    const res = await DriveService.getFileTree("", "shared");
    changeCurrentDrive({ parent: "", sharedDrive: res, driveType: "shared" });
  };

  const loadSelected = ({ parent, drive, sharedDrive }) => {
    if (drive?.Folders)       setTree(t => merge(t, drive, parent));
    if (sharedDrive?.Folders) setSharedTree(t => merge(t, sharedDrive, parent));
  };

  const merge = (t, d, parent) => {
    const ft = [...t];
    d.Folders.forEach(f => {
      const name = f["Prefix"].replace("/", "");
      if (!name) return;
      const meAsParent = parent ? `${parent}/${name}` : name;
      if (!ft.find(x => x.meAsParent === meAsParent)) {
        ft.push({ name, parent, meAsParent });
      }
    });
    return ft;
  };

  const navigateTo = (key, driveType = "") => {
    setActive(key || "home");
    loadFolders(key, driveType);
  };

  /* Navigate to a well-known folder by name (best-effort) */
  const quickNav = (id, folderName) => {
    const node = tree.find(n => n.name === folderName && n.parent === "");
    setActive(id);
    if (node) loadFolders(node.meAsParent);
    else loadFolders("");
  };

  const renderSubs = (parentPath, driveType, treeData) =>
    treeData
      .filter(n => n.parent === parentPath)
      .map(n => (
        <SubItem
          key={n.meAsParent}
          label={n.name}
          active={active === n.meAsParent}
          expanded={expanded}
          onClick={() => navigateTo(n.meAsParent, driveType)}
        />
      ));

  return (
    <div className={styles.root}>

      {/* Brand / logo — click to toggle sidebar */}
      <div className={styles.brand} onClick={onToggle} style={{ cursor: "pointer" }}>
        <div className={styles.brandIcon}><CloudDoneOutlined sx={{ fontSize: 26, color: '#fff' }} /></div>
        {expanded && <span className={styles.brandName}>SmileFy Drive</span>}
      </div>

      {/* Nav */}
      <nav className={styles.nav}>

        {expanded && <div className={styles.sectionLabel}>{t('nav.patients')}</div>}

        <NavItem iconKey="newPatient" label={t('nav.newPatient')}
          active={active === "newPatient"} expanded={expanded}
          onClick={() => { setActive("newPatient"); onNavigate?.('newPatient'); }}
        />

        <NavItem iconKey="myPatients" label={t('nav.myPatients')}
          active={active === "myPatients"} expanded={expanded}
          onClick={() => { setActive("myPatients"); onNavigate?.('myPatients'); }}
        />

        <div className={styles.divider} />

        {expanded && <div className={styles.sectionLabel}>{t('nav.shared')}</div>}

        <NavItem iconKey="shared" label={t('nav.sharedWithMe')}
          active={active === "shared"} expanded={expanded}
          onClick={() => { setActive("shared"); onNavigate?.('drive'); loadShared(); }}
        />

        <div className={styles.divider} />

        {expanded && <div className={styles.sectionLabel}>{t('nav.myDrive')}</div>}

        <NavItem iconKey="home" label={t('nav.allFiles')}
          active={active === "home"} expanded={expanded}
          onClick={() => { setActive("home"); onNavigate?.('drive'); navigateTo(""); }}
        />

      </nav>

      {/* Storage gauge */}
      <div className={styles.bottom}>
        <StorageStatus expanded={expanded} />

        {/* Download Desktop — hidden when already running as Electron */}
        {!window.electronAPI?.isElectron && (
          <Tooltip title={!expanded ? 'Download Desktop App' : ''} placement="right" arrow>
            <a
              href="/downloads/SmileFyDrive-Setup.exe"
              download="SmileFyDrive-Setup.exe"
              style={{ textDecoration: 'none', display: 'block', margin: '6px 0' }}
            >
              {expanded ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px',
                  borderRadius: 10,
                  border: '1px solid rgba(124,106,247,0.5)',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#7c6af7'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(124,106,247,0.5)'}
                >
                  <DesktopWindowsOutlined sx={{ fontSize: 18, color: '#7c6af7', flexShrink: 0 }} />
                  <div>
                    <div style={{ color: 'var(--text-1)', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      Download Desktop App
                    </div>
                    <div style={{ color: 'var(--text-3)', fontSize: 10, whiteSpace: 'nowrap' }}>
                      Windows · Free
                    </div>
                  </div>
                  <FileDownloadOutlined sx={{ fontSize: 14, color: '#7c6af7', marginLeft: 'auto' }} />
                </div>
              ) : (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 38, height: 38, borderRadius: 10, margin: '0 auto',
                  border: '1px solid rgba(124,106,247,0.5)',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#7c6af7'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(124,106,247,0.5)'}
                >
                  <DesktopWindowsOutlined sx={{ fontSize: 18, color: '#7c6af7' }} />
                </div>
              )}
            </a>
          </Tooltip>
        )}

        {/* Divider */}
        <div style={{ height: 1, background: 'var(--border)', margin: '8px 0' }} />

        {/* Profile / Sign out */}
        <Tooltip title={!expanded ? (user ? `${user.Name} ${user.FamilyName}` : t('nav.account')) : ""} placement="right" arrow>
          <div
            onClick={e => setMenuAnchor(e.currentTarget)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: expanded ? '10px 14px' : '10px 0',
              justifyContent: expanded ? 'flex-start' : 'center',
              cursor: 'pointer', borderRadius: 10,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <span style={{
              width: 34, height: 34, borderRadius: '50%',
              background: 'rgba(232,88,120,0.85)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <PersonOutlined sx={{ fontSize: 20, color: '#fff' }} />
            </span>
            {expanded && (
              <div style={{ minWidth: 0 }}>
                <div style={{ color: 'var(--text-1)', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user ? `${user.Name} ${user.FamilyName}` : t('nav.account')}
                </div>
                <div style={{ color: 'var(--text-3)', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.email || t('nav.smilefyUser')}
                </div>
              </div>
            )}
          </div>
        </Tooltip>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          sx={{ mb: 1 }}
          PaperProps={{
            sx: {
              bgcolor: '#1a1a3e',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 2,
              minWidth: 200,
            }
          }}
        >
          {user && (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                {user.Name} {user.FamilyName}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
                {user.email || t('nav.smilefyUser')}
              </Typography>
            </Box>
          )}
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
          <MenuItem onClick={() => setMenuAnchor(null)} sx={{ color: '#c8d0e0', gap: 1.5, fontSize: 14 }}>
            <ManageAccountsOutlined sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />
            {t('nav.profile')}
          </MenuItem>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
          <MenuItem onClick={logOut} sx={{ color: '#e85878', gap: 1.5, fontSize: 14 }}>
            <LogoutOutlined sx={{ fontSize: 18, color: '#e85878' }} />
            {t('nav.signOut')}
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
}
